import React, { FC, HTMLAttributes } from "react";
import { OfferSwitchPage } from "./flow-pages/OfferSwitchPage";
import { CancelPage } from "./flow-pages/cancelPage";
import { SwitchToAnnualForSavingPage } from "./flow-pages/switchToAnnualForSavingPage";
import { SwitchToMonthlyToManageCostPage } from "./flow-pages/switchToMonthlyToManageCostPage";
import { IonButton, IonText } from "@ionic/react";
import { useTranslation } from "react-i18next";

import { withClassName } from "../../../../components/withClassName";
import { ConfirmPage } from "./flow-pages/confirmPage";
import { useCurrentStripePlan } from "../../../../components/hooks";
import { OfferDiscountPage } from "./flow-pages/OfferDiscountPage";

export const Highlight = withClassName("span", "highlight");
export const BigText = withClassName("div", "big-text");

export const SmallText = withClassName("div", "small-text");
export const SmallestText = withClassName("div", "smallest-text");
export const NormalText = withClassName("div", "normal-text");
export const Content = withClassName("div", "content");
export const Bold = withClassName("b", "bold");

export enum FinishPageType {
    OfferSwitch = "offerSwitch",
    OfferSwitchInstant = "offerSwitchInstant",
    OfferDiscount = "offerDiscount",
    Cancelled = "cancelled",
}

// The finish function takes a function that will be called when the user clicks on the finish button
export type FinishFunctionCallback = () => Promise<FinishPageType>;
export type FinishFunction = (
    callback: FinishFunctionCallback
) => Promise<void>;
export type NextFunction = () => void;

export interface FlowPageProps extends HTMLAttributes<any> {
    next?: NextFunction;
    finish?: FinishFunction;
    reset?: () => void;
    reason?: keyof typeof Reasons;
}
export const FlowPage: FC<FlowPageProps> = ({ children, className }) => {
    return (
        <div className="cancel-pages">
            <div className={className}>{children}</div>
        </div>
    );
};
export function amountInDecimal(amount: number, currency: string) {
    return currency.toLowerCase() !== "jpy" ? amount / 100 : amount;
}
export const intervalTOKey = (interval: "month" | "year") => {
    const mapping = {
        month: "stats.stats.month",
        year: "stats.stats.year",
    };
    return mapping[interval];
};
interface CancellationFlowProps {
    pages: FC<FlowPageProps>[];
    reason?: keyof typeof Reasons;
}
const CancellationFlow: FC<CancellationFlowProps> = ({ pages, reason }) => {
    // the last page is a modal, so we need to handle it differently

    // Given a list of children FlowPage components, this component will render the first child and pass the next function to it.
    // The next function will be passed to the next child in the list.
    const [currentPage, setCurrentPage] = React.useState(0);
    const Page = pages[currentPage];
    const next = () => {
        setCurrentPage(currentPage + 1);
    };
    const [finishedPageType, setFinishedPageType] =
        React.useState<FinishPageType | null>(null);
    const finish: FinishFunction = async (callback) => {
        const res = await callback();
        setFinishedPageType(res);
    };
    const reset = () => {
        setCurrentPage(0);
        setFinishedPageType(null);
    };
    return (
        <>
            {finishedPageType ? (
                <ConfirmPage
                    next={next}
                    finish={finish}
                    reset={reset}
                    reason={reason}
                    finishPageType={finishedPageType}
                />
            ) : (
                <Page next={next} finish={finish} reason={reason} />
            )}
        </>
    );
};

export const CancelNextButton: FC<FlowPageProps> = ({ next, children }) => {
    const { t } = useTranslation();

    return (
        <IonButton className={"no-cancel"} onClick={next} fill={"clear"}>
            {children ? (
                children
            ) : (
                <IonText>{t("cancellation.cancelConfirmation")}</IonText>
            )}
        </IonButton>
    );
};

export const Reasons = {
    SeasonEnded: "cancellation.reasons.season-ended",
    Injured: "cancellation.reasons.injury",
    Retired: "cancellation.reasons.retirement",
    TooExpensive: "cancellation.reasons.too-expensive",
    MissingFeature: "cancellation.reasons.missing-feature",
    Other: "cancellation.reasons.other",
} as const;
export interface FlowProps {
    reason: keyof typeof Reasons;
    subscriptionType: "pro" | "basic";
    subscriptionInterval: "month" | "year";
}
export const Flow: FC<FlowProps> = ({
    reason,
    subscriptionType,
    subscriptionInterval,
}) => {
    const currentStripePlan = useCurrentStripePlan();
    const { t } = useTranslation();
    const flows = {
        month: {
            pro: {
                SeasonEnded: [OfferSwitchPage("month"), CancelPage],
                Injured: [CancelPage],
                Retired: [CancelPage],
                TooExpensive: [
                    SwitchToAnnualForSavingPage,
                    OfferSwitchPage("month"),
                    CancelPage,
                ],

                MissingFeature: [OfferSwitchPage("month"), CancelPage],
                Other: [CancelPage],
            },
            basic: {
                SeasonEnded: [CancelPage],
                Injured: [CancelPage],
                Retired: [CancelPage],
                TooExpensive: [SwitchToAnnualForSavingPage, CancelPage],
                MissingFeature: [OfferSwitchPage("month"), CancelPage],
                Other: [CancelPage],
            },
        },
        year: {
            pro: {
                SeasonEnded: [CancelPage],
                Injured: [CancelPage],
                Retired: [CancelPage],
                TooExpensive: [
                    OfferDiscountPage,
                    SwitchToMonthlyToManageCostPage,
                    OfferSwitchPage("year"),
                    CancelPage,
                ],
                MissingFeature: [OfferSwitchPage("year"), CancelPage],
                Other: [CancelPage],
            },
            basic: {
                SeasonEnded: [CancelPage],
                Injured: [CancelPage],
                Retired: [CancelPage],
                TooExpensive: [SwitchToMonthlyToManageCostPage, CancelPage],
                MissingFeature: [OfferSwitchPage("year"), CancelPage],
                Other: [CancelPage],
            },
        },
    };

    const pages = flows[subscriptionInterval][subscriptionType][reason];
    return currentStripePlan === undefined ? (
        <>
            <h1>{t("general.errorTryAgain")}</h1>
        </>
    ) : (
        <CancellationFlow pages={pages} reason={reason} />
    );
};
